import { useEffect } from "react";

interface ScrollableTableOptions {
  scrollerClass: string;
  canScrollLeftClass: string;
  canScrollRightClass: string;
}

export function useScrollableTables(
  ref: React.RefObject<HTMLElement | null>,
  options: ScrollableTableOptions
) {
  const { scrollerClass, canScrollLeftClass, canScrollRightClass } = options;
  const scrollbarClass = "rich-text-react__table-scrollbar";
  const scrollbarThumbClass = "rich-text-react__table-scrollbar-thumb";
  const isScrollableClass = "rich-text-react__table-wrapper--is-scrollable";
  const scrollEpsilonPx = 2;

  useEffect(() => {
    const root = ref.current;
    if (!root) return;

    const initializedScrollers = new WeakSet<Element>();
    const scrollbarCleanups = new Map<HTMLDivElement, () => void>();

    const isNaturallyInteractive = (el: Element): boolean =>
      el.matches(
        "a[href], button, input, select, textarea, summary, [contenteditable='true']"
      );

    const ensureId = (el: Element, fallback: string): string => {
      if (!el.id) el.id = fallback;
      return el.id;
    };

    const removeNonInteractiveTabindex = (rootEl: Element) => {
      if (
        rootEl instanceof HTMLElement &&
        rootEl.hasAttribute("tabindex") &&
        !isNaturallyInteractive(rootEl)
      ) {
        rootEl.removeAttribute("tabindex");
      }

      rootEl.querySelectorAll("[tabindex]").forEach((el) => {
        if (!(el instanceof HTMLElement)) return;
        if (isNaturallyInteractive(el)) return;
        el.removeAttribute("tabindex");
      });
    };

    type CellPlacement = {
      cell: HTMLTableCellElement;
      rowStart: number;
      rowEnd: number;
      colStart: number;
      colEnd: number;
      isHeader: boolean;
      section: "thead" | "tbody" | "tfoot";
    };

    const buildCellPlacements = (table: HTMLTableElement): CellPlacement[] => {
      const placements: CellPlacement[] = [];
      const occupiedRowsPerColumn: number[] = [];
      const rows = Array.from(table.rows);

      rows.forEach((row, rowIndex) => {
        for (let col = 0; col < occupiedRowsPerColumn.length; col++) {
          if (occupiedRowsPerColumn[col] > 0) occupiedRowsPerColumn[col] -= 1;
        }

        let columnCursor = 0;
        const sectionTag = row.parentElement?.tagName.toLowerCase();
        const section: "thead" | "tbody" | "tfoot" =
          sectionTag === "thead"
            ? "thead"
            : sectionTag === "tfoot"
              ? "tfoot"
              : "tbody";

        Array.from(row.cells).forEach((cell) => {
          while (occupiedRowsPerColumn[columnCursor] > 0) {
            columnCursor += 1;
          }

          const colSpan = Math.max(1, cell.colSpan || 1);
          const rowSpan = Math.max(1, cell.rowSpan || 1);
          const colStart = columnCursor;
          const colEnd = columnCursor + colSpan - 1;

          for (let col = colStart; col <= colEnd; col++) {
            occupiedRowsPerColumn[col] = Math.max(
              occupiedRowsPerColumn[col] ?? 0,
              rowSpan - 1
            );
          }

          placements.push({
            cell,
            rowStart: rowIndex,
            rowEnd: rowIndex + rowSpan - 1,
            colStart,
            colEnd,
            isHeader: cell.tagName.toLowerCase() === "th",
            section,
          });

          columnCursor += colSpan;
        });
      });

      return placements;
    };

    const normalizeTableHeaders = (table: HTMLTableElement) => {
      removeNonInteractiveTabindex(table);

      const placements = buildCellPlacements(table);
      if (placements.length === 0) return;

      const tableId = ensureId(
        table,
        `rich-table-${Math.random().toString(36).slice(2, 8)}`
      );

      const maxColumn = placements.reduce(
        (max, p) => Math.max(max, p.colEnd),
        0
      );

      // A cell that is alone on its row and spans the full width of the table
      // is a sub heading for the rows below it - for instance the department
      // that the directorates underneath it belong to. Following "Tables with
      // multi-level headers" (example 2) every data cell in the group points
      // back at that sub heading through the headers attribute, so a screen
      // reader announces it along with the column and row headers.
      const cellsOnRow = (row: number) =>
        placements.filter((p) => p.rowStart <= row && p.rowEnd >= row);

      const groupHeadings = placements.filter(
        (p) =>
          p.section === "tbody" &&
          maxColumn > 0 &&
          p.colStart === 0 &&
          p.colEnd === maxColumn &&
          cellsOnRow(p.rowStart).length === 1
      );
      const groupHeadingCells = new Set(groupHeadings.map((p) => p.cell));

      groupHeadings.forEach((group, idx) => {
        const cell = group.cell;
        ensureId(cell, `${tableId}-g-${idx + 1}`);

        if (group.isHeader) {
          if (!cell.hasAttribute("scope")) {
            cell.setAttribute("scope", "colgroup");
          }
        } else if (!cell.hasAttribute("role")) {
          // editors sometimes build the sub heading row from a plain data cell
          cell.setAttribute("role", "columnheader");
        }

        cell.removeAttribute("headers");
      });

      const headerPlacements = placements.filter(
        (p) => p.isHeader && !groupHeadingCells.has(p.cell)
      );
      headerPlacements.forEach((header, idx) => {
        const th = header.cell as HTMLTableCellElement;

        ensureId(th, `${tableId}-h-${idx + 1}`);

        if (!th.hasAttribute("scope")) {
          if (header.section === "thead") {
            th.setAttribute("scope", th.colSpan > 1 ? "colgroup" : "col");
          } else {
            const isFirstColumnHeader = header.colStart === 0;
            if (isFirstColumnHeader) {
              th.setAttribute("scope", th.rowSpan > 1 ? "rowgroup" : "row");
            } else {
              th.setAttribute("scope", th.colSpan > 1 ? "colgroup" : "col");
            }
          }
        }
      });

      const colHeadersByColumn: string[][] = Array.from(
        { length: maxColumn + 1 },
        () => []
      );

      headerPlacements
        .filter((h) => {
          const scope = h.cell.getAttribute("scope");
          return (
            scope === "col" || scope === "colgroup" || h.section === "thead"
          );
        })
        .forEach((h) => {
          const id = h.cell.id;
          if (!id) return;
          for (let col = h.colStart; col <= h.colEnd; col++) {
            if (!colHeadersByColumn[col].includes(id)) {
              colHeadersByColumn[col].push(id);
            }
          }
        });

      const rowHeaderPlacements = headerPlacements.filter((h) => {
        const scope = h.cell.getAttribute("scope");
        return scope === "row" || scope === "rowgroup";
      });

      const groupHeadingForRow = (row: number): CellPlacement | undefined =>
        groupHeadings.reduce<CellPlacement | undefined>(
          (current, group) => (group.rowEnd < row ? group : current),
          undefined
        );

      placements
        .filter((p) => !p.isHeader && !groupHeadingCells.has(p.cell))
        .forEach((cellPlacement) => {
          const headers: string[] = [];
          const addHeader = (id: string | undefined) => {
            if (id && !headers.includes(id)) headers.push(id);
          };

          addHeader(groupHeadingForRow(cellPlacement.rowStart)?.cell.id);

          for (
            let col = cellPlacement.colStart;
            col <= cellPlacement.colEnd;
            col++
          ) {
            colHeadersByColumn[col]?.forEach(addHeader);
          }

          rowHeaderPlacements.forEach((rowHeader) => {
            if (
              cellPlacement.rowStart >= rowHeader.rowStart &&
              cellPlacement.rowStart <= rowHeader.rowEnd
            ) {
              addHeader(rowHeader.cell.id);
            }
          });

          if (headers.length > 0) {
            cellPlacement.cell.setAttribute("headers", headers.join(" "));
          } else {
            cellPlacement.cell.removeAttribute("headers");
          }
        });
    };

    let pendingResizeFrame = 0;
    const onResize = () => {
      if (pendingResizeFrame) return;
      pendingResizeFrame = window.requestAnimationFrame(() => {
        pendingResizeFrame = 0;
        updateAllScrollStates();
        updateScrollbars();
      });
    };

    const resizeObserver = new ResizeObserver(onResize);

    const isScrollerActuallyScrollable = (scroller: Element): boolean =>
      scroller.scrollWidth - scroller.clientWidth > scrollEpsilonPx;

    // A table that fits must stay out of the tab order entirely - there is
    // nothing to operate. As soon as it really does scroll the container has to
    // be reachable by keyboard instead (WCAG 2.1.1 Keyboard), and it is then
    // named after the table caption so it is not an anonymous stop.
    const setScrollerFocusability = (
      scroller: Element,
      isScrollable: boolean
    ) => {
      if (!(scroller instanceof HTMLElement)) return;

      if (!isScrollable) {
        scroller.removeAttribute("tabindex");
        scroller.removeAttribute("role");
        scroller.removeAttribute("aria-labelledby");
        return;
      }

      scroller.setAttribute("tabindex", "0");

      const table = scroller.querySelector("table");
      const caption = table?.querySelector("caption");

      if (table instanceof HTMLTableElement && caption instanceof HTMLElement) {
        scroller.setAttribute("role", "region");
        scroller.setAttribute(
          "aria-labelledby",
          ensureId(caption, `${table.id}-caption`)
        );
      }
    };

    const setScrollState = (scroller: Element) => {
      const wrapper = scroller.parentElement?.classList;
      if (!wrapper) return;

      const bufferPx = scrollEpsilonPx;
      const scrollLeft = scroller.scrollLeft;
      const maxScrollLeft = scroller.scrollWidth - scroller.clientWidth;
      const isScrollable = isScrollerActuallyScrollable(scroller);

      wrapper.toggle(isScrollableClass, isScrollable);
      setScrollerFocusability(scroller, isScrollable);

      if (!isScrollable) {
        wrapper.remove(canScrollLeftClass);
        wrapper.remove(canScrollRightClass);
        return;
      }

      wrapper.toggle(canScrollLeftClass, scrollLeft > 0);
      wrapper.toggle(
        canScrollRightClass,
        scrollLeft + bufferPx < maxScrollLeft
      );
    };

    const onScrollerScroll = (e: Event) => {
      setScrollState(e.target as Element);
    };

    const initializeScroller = (scroller: Element) => {
      if (initializedScrollers.has(scroller)) return;
      removeNonInteractiveTabindex(scroller);
      const table = scroller.querySelector("table");
      if (table instanceof HTMLTableElement) {
        normalizeTableHeaders(table);
        // Text-only zoom resizes the table without resizing the window, so the
        // table has to be watched as well as the scroller.
        resizeObserver.observe(table);
      }
      resizeObserver.observe(scroller);
      scroller.addEventListener("scroll", onScrollerScroll);
      setScrollState(scroller);
      initializedScrollers.add(scroller);
    };

    const cleanupScroller = (scroller: Element) => {
      setScrollerFocusability(scroller, false);
      resizeObserver.unobserve(scroller);
      const table = scroller.querySelector("table");
      if (table) resizeObserver.unobserve(table);
      scroller.removeEventListener("scroll", onScrollerScroll);
      initializedScrollers.delete(scroller);
    };

    const getAllScrollers = (root: HTMLElement | null): Element[] => {
      return Array.from(root?.getElementsByClassName(scrollerClass) ?? []);
    };

    // update scroll state on resize or DOM mutation
    const updateAllScrollStates = () => {
      getAllScrollers(root).forEach(setScrollState);
    };

    // ---- CUSTOM SCROLLBAR LOGIC ----
    type ScrollbarDragState = {
      pointerId: number;
      tableScroller: HTMLDivElement;
      scrollbar: HTMLDivElement;
      thumb: HTMLDivElement;
      dragOffset: number;
    };

    let activeDrag: ScrollbarDragState | null = null;

    const updateThumb = (
      tableScroller: HTMLDivElement,
      scrollbar: HTMLDivElement,
      scrollbarThumb: HTMLDivElement
    ) => {
      const maxScrollLeft =
        tableScroller.scrollWidth - tableScroller.clientWidth;

      if (maxScrollLeft <= 0) {
        scrollbarThumb.style.width = "100%";
        scrollbarThumb.style.left = "0";
        return;
      }

      const visibleRatio =
        tableScroller.clientWidth / tableScroller.scrollWidth;
      const thumbWidth = Math.max(visibleRatio * scrollbar.clientWidth, 20);
      scrollbarThumb.style.width = `${thumbWidth}px`;

      const rawScrollerRatio = tableScroller.scrollLeft / maxScrollLeft;
      const scrollRatio = Math.max(0, Math.min(1, rawScrollerRatio));

      const maxThumbMove = scrollbar.clientWidth - thumbWidth;
      scrollbarThumb.style.left = `${scrollRatio * maxThumbMove}px`;
    };

    const setScrollFromPointer = (
      event: PointerEvent,
      dragState: ScrollbarDragState
    ) => {
      const { tableScroller, scrollbar, thumb, dragOffset } = dragState;
      const maxScrollLeft =
        tableScroller.scrollWidth - tableScroller.clientWidth;
      const maxThumbMove = scrollbar.clientWidth - thumb.offsetWidth;

      if (maxScrollLeft <= 0 || maxThumbMove <= 0) return;

      const scrollbarRect = scrollbar.getBoundingClientRect();
      const rawThumbLeft = event.clientX - scrollbarRect.left - dragOffset;
      const thumbLeft = Math.max(0, Math.min(maxThumbMove, rawThumbLeft));
      const scrollRatio = thumbLeft / maxThumbMove;

      tableScroller.scrollLeft = scrollRatio * maxScrollLeft;
    };

    const endScrollbarDrag = (event?: PointerEvent) => {
      if (!activeDrag) return;
      if (event && event.pointerId !== activeDrag.pointerId) return;

      const { scrollbar, thumb, pointerId } = activeDrag;
      scrollbar.classList.remove(`${scrollbarClass}--dragging`);
      thumb.classList.remove(`${scrollbarThumbClass}--dragging`);

      if (scrollbar.hasPointerCapture(pointerId)) {
        scrollbar.releasePointerCapture(pointerId);
      }

      activeDrag = null;
    };

    const onScrollbarPointerMove = (event: PointerEvent) => {
      if (!activeDrag || event.pointerId !== activeDrag.pointerId) return;
      setScrollFromPointer(event, activeDrag);
    };

    const onScrollbarPointerEnd = (event: PointerEvent) => {
      endScrollbarDrag(event);
    };

    const createScrollbarForScroller = (tableScroller: HTMLDivElement) => {
      const wrapper = tableScroller.parentElement;
      if (!wrapper) return;

      const scrollbar = document.createElement("div");
      scrollbar.className = scrollbarClass;

      const scrollbarThumb = document.createElement("div");
      scrollbarThumb.className = scrollbarThumbClass;

      wrapper.appendChild(scrollbar);
      scrollbar.appendChild(scrollbarThumb);

      const updateThumbPosition = () =>
        updateThumb(tableScroller, scrollbar, scrollbarThumb);

      const onScrollbarPointerDown = (event: PointerEvent) => {
        if (event.button !== 0) return;

        const maxScrollLeft =
          tableScroller.scrollWidth - tableScroller.clientWidth;
        if (maxScrollLeft <= 0) return;

        const eventTarget = event.target;
        const thumbRect = scrollbarThumb.getBoundingClientRect();
        const dragOffset =
          eventTarget instanceof Node && scrollbarThumb.contains(eventTarget)
            ? event.clientX - thumbRect.left
            : thumbRect.width / 2;

        endScrollbarDrag();
        const dragState = {
          pointerId: event.pointerId,
          tableScroller,
          scrollbar,
          thumb: scrollbarThumb,
          dragOffset,
        };
        activeDrag = dragState;

        scrollbar.classList.add(`${scrollbarClass}--dragging`);
        scrollbarThumb.classList.add(`${scrollbarThumbClass}--dragging`);
        scrollbar.setPointerCapture(event.pointerId);
        setScrollFromPointer(event, dragState);
        event.preventDefault();
      };

      tableScroller.addEventListener("scroll", updateThumbPosition);
      scrollbar.addEventListener("pointerdown", onScrollbarPointerDown);

      updateThumbPosition();

      const cleanupScrollbar = () => {
        if (activeDrag?.scrollbar === scrollbar) {
          endScrollbarDrag();
        }

        tableScroller.removeEventListener("scroll", updateThumbPosition);
        scrollbar.removeEventListener("pointerdown", onScrollbarPointerDown);
        scrollbar.remove();
        scrollbarCleanups.delete(scrollbar);
      };

      scrollbarCleanups.set(scrollbar, cleanupScrollbar);
    };

    const updateScrollbars = () => {
      getAllScrollers(root).forEach((scroller) => {
        if (!(scroller instanceof HTMLDivElement)) return;

        const wrapper = scroller.parentElement;
        if (!wrapper) return;

        const scrollbar = wrapper.getElementsByClassName(scrollbarClass)[0] as
          | HTMLDivElement
          | undefined;
        const isScrollable = isScrollerActuallyScrollable(scroller);

        if (isScrollable && !scrollbar) {
          createScrollbarForScroller(scroller);
        } else if (!isScrollable && scrollbar) {
          const cleanupScrollbar = scrollbarCleanups.get(scrollbar);
          if (cleanupScrollbar) {
            cleanupScrollbar();
          } else {
            scrollbar.remove();
          }
        } else if (isScrollable && scrollbar) {
          const scrollbarThumb = scrollbar.getElementsByClassName(
            scrollbarThumbClass
          )[0] as HTMLDivElement | undefined;
          if (scrollbarThumb) {
            updateThumb(scroller, scrollbar, scrollbarThumb);
          }
        }
      });
    };

    // ---- SETUP ----
    window.addEventListener("resize", onResize);
    window.addEventListener("pointermove", onScrollbarPointerMove);
    window.addEventListener("pointerup", onScrollbarPointerEnd);
    window.addEventListener("pointercancel", onScrollbarPointerEnd);

    const observer = new MutationObserver(() => {
      getAllScrollers(root).forEach(initializeScroller);
      updateAllScrollStates();
      updateScrollbars();
    });

    observer.observe(root, { childList: true, subtree: true });

    getAllScrollers(root).forEach(initializeScroller);
    updateAllScrollStates();
    updateScrollbars();

    // ---- CLEANUP ----
    return () => {
      endScrollbarDrag();
      getAllScrollers(root).forEach(cleanupScroller);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("pointermove", onScrollbarPointerMove);
      window.removeEventListener("pointerup", onScrollbarPointerEnd);
      window.removeEventListener("pointercancel", onScrollbarPointerEnd);
      if (pendingResizeFrame) window.cancelAnimationFrame(pendingResizeFrame);
      resizeObserver.disconnect();
      observer.disconnect();
      for (const cleanupScrollbar of Array.from(scrollbarCleanups.values())) {
        cleanupScrollbar();
      }
    };
  }, [ref, scrollerClass, canScrollLeftClass, canScrollRightClass]);
}
